import React from "react";
import {
  Users,
  Briefcase,
  FileText,
  TrendingUp,
  UserCheck,
  Building,
  Shield,
  CreditCard,
  BarChart3,
  Target,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import adminApi from "../../api/admin";

// Helper function to format dates
const formatMonth = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const AdminDashboard = () => {
  const {
    data: overviewData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["overview-stats"],
    queryFn: async () => {
      const res = await adminApi.getAdminDashboardOverview();
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border">
                <div className="w-8 h-8 bg-gray-200 rounded mb-3"></div>
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            Error loading dashboard data: {error?.message}
          </p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const jobPostingTrend =
    overviewData?.charts?.job_posting_trend?.map((item) => ({
      month: formatMonth(item.month),
      count: item.count,
    })) || [];

  const applicationTrend =
    overviewData?.charts?.application_trend?.map((item) => ({
      month: formatMonth(item.month),
      count: item.count,
    })) || [];

  const revenueTrend =
    overviewData?.charts?.revenue_trend?.map((item) => ({
      month: formatMonth(item.month),
      revenue: item.total,
    })) || [];

  const stats = [
    {
      label: "Total Users",
      value: overviewData?.overview?.total_users || 0,
      icon: Users,
      color: "text-blue-500",
    },
    {
      label: "Active Jobs",
      value: overviewData?.overview?.active_job_postings || 0,
      icon: Briefcase,
      color: "text-green-500",
    },
    {
      label: "Pending Applications",
      value: overviewData?.overview?.pending_applications || 0,
      icon: FileText,
      color: "text-yellow-500",
    },
    {
      label: "Total Revenue",
      value: `$${overviewData?.revenue?.total_revenue || 0}`,
      icon: TrendingUp,
      color: "text-purple-500",
    },
  ];

  const userStats = [
    {
      label: "Professionals",
      value: overviewData?.overview?.total_professionals || 0,
      icon: UserCheck,
      color: "text-indigo-500",
    },
    {
      label: "Recruiters",
      value: overviewData?.overview?.total_recruiters || 0,
      icon: Building,
      color: "text-orange-500",
    },
    {
      label: "Admins",
      value: overviewData?.overview?.total_admins || 0,
      icon: Shield,
      color: "text-red-500",
    },
    {
      label: "Active Subscriptions",
      value: overviewData?.overview?.total_subscriptions || 0,
      icon: CreditCard,
      color: "text-green-500",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-secondary-900 mb-6">
        Admin Dashboard
      </h1>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow"
          >
            <stat.icon className={`w-8 h-8 ${stat.color} mb-3`} />
            <p className="text-3xl font-bold text-secondary-900">
              {stat.value}
            </p>
            <p className="text-secondary-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Job Posting Trend */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="font-bold text-xl mb-4 text-secondary-900">
            Job Posting Trend
          </h3>
          {jobPostingTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={jobPostingTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Job Postings" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-secondary-500">
              No job posting data available
            </div>
          )}
        </div>

        {/* Application Trend */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="font-bold text-xl mb-4 text-secondary-900">
            Application Trend
          </h3>
          {applicationTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={applicationTrend}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                barCategoryGap={applicationTrend.length <= 2 ? "50%" : "30%"}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} interval={0} />
                <YAxis />
                <Tooltip
                  cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
                  formatter={(value) => [value, "Applications"]}
                />
                <Bar
                  dataKey="count"
                  fill="#10b981"
                  name="Applications"
                  radius={[4, 4, 0, 0]}
                  barSize={applicationTrend.length <= 2 ? 60 : 40}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-secondary-500 bg-gray-50 rounded-lg">
              <div className="text-center">
                <TrendingUp className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm">No application data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revenue Trend */}
      <div className="bg-white p-6 rounded-xl shadow-sm border mb-8">
        <h3 className="font-bold text-xl mb-4 text-secondary-900">
          Revenue Trend
        </h3>
        {revenueTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, "Revenue"]} />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#ff7300"
                name="Revenue ($)"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-secondary-500">
            No revenue data available
          </div>
        )}
      </div>

      {/* User Analytics */}
      <div className="bg-white p-6 rounded-xl shadow-sm border mb-8">
        <h3 className="font-bold text-xl mb-4 text-secondary-900">
          User Analytics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {userStats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-4 bg-gray-50 rounded-lg"
            >
              <stat.icon className={`w-6 h-6 ${stat.color} mx-auto mb-2`} />
              <p className="text-2xl font-bold text-secondary-900">
                {stat.value}
              </p>
              <p className="text-sm text-secondary-600">{stat.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="font-semibold text-blue-900">Verified Users</p>
            <p className="text-xl font-bold">
              {overviewData?.users?.verification_status?.verified || 0}
            </p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="font-semibold text-yellow-900">Unverified Users</p>
            <p className="text-xl font-bold">
              {overviewData?.users?.verification_status?.unverified || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Application Status */}
      <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border">
        <h3 className="font-bold text-xl mb-4 text-secondary-900">
          Application Status Breakdown
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {overviewData?.job_applications?.status_breakdown &&
            Object.entries(overviewData.job_applications.status_breakdown).map(
              ([status, count]) => (
                <div
                  key={status}
                  className="text-center p-3 bg-gray-50 rounded-lg"
                >
                  <p className="text-lg font-bold text-secondary-900">
                    {count}
                  </p>
                  <p className="text-xs text-secondary-600 capitalize">
                    {status.replace("_", " ")}
                  </p>
                </div>
              )
            )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
